import { Bike, Loader2, Package, ShoppingBag } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SectionHeader } from '@/components/ui/section-header';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { StatusPill, type StatusTone } from '@/components/ui/status-pill';
import { form_error_message } from '@/lib/forms';
import { use_vendor_orders, use_transition_order } from '@/features/order/hooks';
import type { Order, OrderIntent, OrderStatus } from '@/features/order/types';

const STATUS_TONE: Record<OrderStatus, StatusTone> = {
  PLACED: 'warning',
  ACCEPTED: 'info',
  OUT_FOR_DELIVERY: 'info',
  READY: 'info',
  COMPLETED: 'success',
  CANCELLED: 'destructive',
  REJECTED: 'destructive',
};

/** Next actions available from a given status, by fulfilment type. */
function actions(o: Order): { intent: OrderIntent; label: string; variant?: 'gradient' | 'outline' | 'ghost' }[] {
  switch (o.status) {
    case 'PLACED':
      return [
        { intent: 'accept', label: 'Accept', variant: 'gradient' },
        { intent: 'reject', label: 'Reject', variant: 'ghost' },
      ];
    case 'ACCEPTED':
      return [
        o.fulfillment_type === 'DELIVERY'
          ? { intent: 'out-for-delivery', label: 'Out for delivery', variant: 'gradient' }
          : { intent: 'ready', label: 'Mark ready', variant: 'gradient' },
        { intent: 'cancel', label: 'Cancel', variant: 'ghost' },
      ];
    case 'OUT_FOR_DELIVERY':
    case 'READY':
      return [{ intent: 'complete', label: 'Mark completed', variant: 'gradient' }];
    default:
      return [];
  }
}

function OrderCard({ order }: { order: Order }) {
  const transition = use_transition_order();
  const act = (intent: OrderIntent) => {
    const reason = intent === 'reject' || intent === 'cancel' ? window.prompt('Reason (optional):') ?? undefined : undefined;
    transition.mutate({ id: order.id, intent, reason: reason?.trim() || undefined });
  };

  return (
    <Card data-testid={`vendor-order-${order.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border p-4">
        <div className="flex items-center gap-2">
          {order.fulfillment_type === 'DELIVERY' ? <Bike className="h-5 w-5 text-primary" /> : <Package className="h-5 w-5 text-primary" />}
          <span className="font-semibold">{order.fulfillment_type === 'DELIVERY' ? 'Delivery' : 'Pickup'}</span>
          <StatusPill tone={STATUS_TONE[order.status]}>{order.status.replace(/_/g, ' ').toLowerCase()}</StatusPill>
        </div>
        <span className="font-heading text-lg font-bold tabular-nums">₹{order.total_inr}</span>
      </div>

      <div className="p-4">
        <ul className="mb-3 flex flex-col gap-1 text-sm">
          {order.items.map((it) => (
            <li key={it.id} className="flex justify-between">
              <span>
                {it.quantity}× {it.name}
              </span>
              <span className="tabular-nums text-muted-foreground">₹{it.line_total_inr}</span>
            </li>
          ))}
        </ul>
        <div className="text-xs text-muted-foreground">
          <span>📞 {order.contact_phone}</span>
          {order.fulfillment_type === 'DELIVERY' && order.address && (
            <span className="ml-3">📍 {[order.address, order.landmark, order.city, order.pincode].filter(Boolean).join(', ')}</span>
          )}
          {order.notes && <p className="mt-1">Note: {order.notes}</p>}
        </div>

        {actions(order).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {actions(order).map((a) => (
              <Button
                key={a.intent}
                size="sm"
                variant={a.variant}
                disabled={transition.isPending}
                onClick={() => act(a.intent)}
                data-testid={`order-${a.intent}-${order.id}`}
              >
                {a.label}
              </Button>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

export function VendorOrdersPage() {
  const orders_q = use_vendor_orders();
  const orders = orders_q.data ?? [];

  return (
    <div className="flex flex-col gap-5">
      <SectionHeader title="Orders" description="Incoming food orders — you manage fulfilment and delivery." />

      {orders_q.isLoading ? (
        <Card>
          <div className="flex items-center justify-center p-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        </Card>
      ) : orders_q.error ? (
        <ErrorState data-testid="orders-error" title="Couldn't load orders" description={form_error_message(orders_q.error, 'Please try again.')} />
      ) : orders.length === 0 ? (
        <EmptyState data-testid="orders-empty" icon={ShoppingBag} title="No orders yet" description="New food orders will appear here." />
      ) : (
        <div className="flex flex-col gap-4" data-testid="vendor-orders-list">
          {orders.map((o) => (
            <OrderCard key={o.id} order={o} />
          ))}
        </div>
      )}
    </div>
  );
}
